import React, { useState, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  HomeIcon,
  DocumentTextIcon,
  UserGroupIcon,
  UsersIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  ArrowRightOnRectangleIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
  Bars3Icon,
  XMarkIcon,
  ClockIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const { user, logout, canViewVendors, canViewInvoices, canViewEmployees, canViewCustomers, canViewARInvoices, canViewChannels, hasDirectReports } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [financeDropdownOpen, setFinanceDropdownOpen] = useState(false);
  const [customersDropdownOpen, setCustomersDropdownOpen] = useState(false);
  const [vendorsDropdownOpen, setVendorsDropdownOpen] = useState(false);
  const [hrDropdownOpen, setHrDropdownOpen] = useState(false);
  const [settingsSubmenuOpen, setSettingsSubmenuOpen] = useState(false);
  const [channelsSubmenuOpen, setChannelsSubmenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Refs for dropdown close timers
  const financeCloseTimer = useRef(null);
  const customersCloseTimer = useRef(null);
  const vendorsCloseTimer = useRef(null);
  const hrCloseTimer = useRef(null);
  const settingsCloseTimer = useRef(null);
  const channelsCloseTimer = useRef(null);
  const userCloseTimer = useRef(null);

  // Finance dropdown handlers with delay
  const handleFinanceMouseEnter = useCallback(() => {
    if (financeCloseTimer.current) {
      clearTimeout(financeCloseTimer.current);
      financeCloseTimer.current = null;
    }
    setFinanceDropdownOpen(true);
  }, []);

  const handleFinanceMouseLeave = useCallback(() => {
    financeCloseTimer.current = setTimeout(() => {
      setFinanceDropdownOpen(false);
      setSettingsSubmenuOpen(false);
    }, 150);
  }, []);

  // Customers dropdown handlers with delay
  const handleCustomersMouseEnter = useCallback(() => {
    if (customersCloseTimer.current) {
      clearTimeout(customersCloseTimer.current);
      customersCloseTimer.current = null;
    }
    setCustomersDropdownOpen(true);
  }, []);

  const handleCustomersMouseLeave = useCallback(() => {
    customersCloseTimer.current = setTimeout(() => {
      setCustomersDropdownOpen(false);
      setChannelsSubmenuOpen(false);
    }, 150);
  }, []);

  // Vendors dropdown handlers with delay
  const handleVendorsMouseEnter = useCallback(() => {
    if (vendorsCloseTimer.current) {
      clearTimeout(vendorsCloseTimer.current);
      vendorsCloseTimer.current = null;
    }
    setVendorsDropdownOpen(true);
  }, []);

  const handleVendorsMouseLeave = useCallback(() => {
    vendorsCloseTimer.current = setTimeout(() => {
      setVendorsDropdownOpen(false);
    }, 150);
  }, []);

  // HR dropdown handlers with delay
  const handleHrMouseEnter = useCallback(() => {
    if (hrCloseTimer.current) {
      clearTimeout(hrCloseTimer.current);
      hrCloseTimer.current = null;
    }
    setHrDropdownOpen(true);
  }, []);

  const handleHrMouseLeave = useCallback(() => {
    hrCloseTimer.current = setTimeout(() => {
      setHrDropdownOpen(false);
    }, 150);
  }, []);

  // Settings submenu handlers with delay
  const handleSettingsMouseEnter = useCallback(() => {
    if (settingsCloseTimer.current) {
      clearTimeout(settingsCloseTimer.current);
      settingsCloseTimer.current = null;
    }
    setSettingsSubmenuOpen(true);
  }, []);

  const handleSettingsMouseLeave = useCallback(() => {
    settingsCloseTimer.current = setTimeout(() => {
      setSettingsSubmenuOpen(false);
    }, 150);
  }, []);

  // Channels submenu handlers with delay
  const handleChannelsMouseEnter = useCallback(() => {
    if (channelsCloseTimer.current) {
      clearTimeout(channelsCloseTimer.current);
      channelsCloseTimer.current = null;
    }
    setChannelsSubmenuOpen(true);
  }, []);

  const handleChannelsMouseLeave = useCallback(() => {
    channelsCloseTimer.current = setTimeout(() => {
      setChannelsSubmenuOpen(false);
    }, 150);
  }, []);

  // User dropdown handlers with delay
  const handleUserMouseEnter = useCallback(() => {
    if (userCloseTimer.current) {
      clearTimeout(userCloseTimer.current);
      userCloseTimer.current = null;
    }
    setUserDropdownOpen(true);
  }, []);

  const handleUserMouseLeave = useCallback(() => {
    userCloseTimer.current = setTimeout(() => {
      setUserDropdownOpen(false);
    }, 150);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    closeMobileMenu();
  };

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <div className="navbar-brand">
          <NavLink to="/" className="navbar-logo">
            <span className="navbar-logo-icon">
              <BriefcaseIcon style={{ width: 20, height: 20, color: 'white' }} />
            </span>
            Scalepunk ERP
          </NavLink>
          {/* Mobile menu toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <XMarkIcon style={{ width: 24, height: 24 }} />
            ) : (
              <Bars3Icon style={{ width: 24, height: 24 }} />
            )}
          </button>
          <div className={`navbar-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <NavLink
              to="/"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={closeMobileMenu}
              end
            >
              <HomeIcon style={{ width: 18, height: 18 }} />
              Dashboard
            </NavLink>

            {/* Finance Dropdown */}
            {canViewInvoices && (
              <div
                className="nav-dropdown"
                onMouseEnter={handleFinanceMouseEnter}
                onMouseLeave={handleFinanceMouseLeave}
              >
                <button className={`nav-link ${(location.pathname.includes('/finance') || location.pathname.includes('/projects') || location.pathname.includes('/companies')) ? 'active' : ''}`}>
                  <BanknotesIcon style={{ width: 18, height: 18 }} />
                  Finance
                  <ChevronDownIcon style={{ width: 14, height: 14, marginLeft: 4 }} />
                </button>
                {financeDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <NavLink
                      to="/finance/fx-rates"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                    >
                      <CurrencyDollarIcon style={{ width: 16, height: 16 }} />
                      Foreign Exchange
                    </NavLink>
                    <NavLink
                      to="/projects"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                    >
                      <BriefcaseIcon style={{ width: 16, height: 16 }} />
                      Projects
                    </NavLink>
                    <NavLink
                      to="/companies"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                    >
                      <BuildingOfficeIcon style={{ width: 16, height: 16 }} />
                      Companies
                    </NavLink>
                    <div className="nav-dropdown-divider" />
                    <div
                      className="nav-dropdown-item-with-submenu"
                      onMouseEnter={handleSettingsMouseEnter}
                      onMouseLeave={handleSettingsMouseLeave}
                    >
                      <div className={`nav-dropdown-item ${(location.pathname.includes('/finance/cost-centers') || location.pathname.includes('/finance/chart-of-accounts') || location.pathname.includes('/finance/banks')) ? 'active' : ''}`}>
                        <Cog6ToothIcon style={{ width: 16, height: 16 }} />
                        Settings
                        <ChevronRightIcon style={{ width: 14, height: 14, marginLeft: 'auto' }} />
                      </div>
                      <div className="nav-dropdown-submenu" style={{ display: settingsSubmenuOpen ? 'block' : 'none' }}>
                        <NavLink
                          to="/finance/cost-centers"
                          className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                        >
                          Cost Centers
                        </NavLink>
                        <NavLink
                          to="/finance/chart-of-accounts"
                          className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                        >
                          Chart of Accounts
                        </NavLink>
                        <NavLink
                          to="/finance/banks"
                          className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                        >
                          Banks
                        </NavLink>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Customers Dropdown */}
            {(canViewCustomers || canViewARInvoices || canViewChannels) && (
              <div
                className="nav-dropdown"
                onMouseEnter={handleCustomersMouseEnter}
                onMouseLeave={handleCustomersMouseLeave}
              >
                <button className={`nav-link ${(location.pathname.includes('/customers') || location.pathname.includes('/ar-invoices')) ? 'active' : ''}`}>
                  <UserGroupIcon style={{ width: 18, height: 18 }} />
                  Customers
                  <ChevronDownIcon style={{ width: 14, height: 14, marginLeft: 4 }} />
                </button>
                {customersDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    {canViewARInvoices && (
                      <NavLink
                        to="/ar-invoices"
                        className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                      >
                        <BanknotesIcon style={{ width: 16, height: 16 }} />
                        AR Invoices
                      </NavLink>
                    )}
                    {canViewCustomers && (
                      <>
                        <NavLink
                          to="/customers/monthly-statistics"
                          className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                        >
                          <CalendarDaysIcon style={{ width: 16, height: 16 }} />
                          Monthly Statistics
                        </NavLink>
                        <NavLink
                          to="/customers/management"
                          className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                        >
                          <UserGroupIcon style={{ width: 16, height: 16 }} />
                          Customer Management
                        </NavLink>
                      </>
                    )}
                    {canViewChannels && (
                      <>
                        <div className="nav-dropdown-divider" />
                        <div
                          className="nav-dropdown-item-with-submenu"
                          onMouseEnter={handleChannelsMouseEnter}
                          onMouseLeave={handleChannelsMouseLeave}
                        >
                          <div className={`nav-dropdown-item ${(location.pathname.includes('/customers/channel-report') || location.pathname.includes('/customers/channel-management')) ? 'active' : ''}`}>
                            <ChartBarIcon style={{ width: 16, height: 16 }} />
                            Channels
                            <ChevronRightIcon style={{ width: 14, height: 14, marginLeft: 'auto' }} />
                          </div>
                          <div className="nav-dropdown-submenu" style={{ display: channelsSubmenuOpen ? 'block' : 'none' }}>
                            <NavLink
                              to="/customers/channel-report"
                              className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                            >
                              Channel Report
                            </NavLink>
                            <NavLink
                              to="/customers/channel-management"
                              className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                            >
                              Channel Management
                            </NavLink>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Vendors Dropdown */}
            {canViewVendors && (
              <div
                className="nav-dropdown"
                onMouseEnter={handleVendorsMouseEnter}
                onMouseLeave={handleVendorsMouseLeave}
              >
                <button className={`nav-link ${(location.pathname.includes('/vendors') || location.pathname.includes('/invoices')) && !location.pathname.includes('/ar-invoices') ? 'active' : ''}`}>
                  <UserGroupIcon style={{ width: 18, height: 18 }} />
                  Vendors
                  <ChevronDownIcon style={{ width: 14, height: 14, marginLeft: 4 }} />
                </button>
                {vendorsDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <NavLink
                      to="/invoices"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                    >
                      <DocumentTextIcon style={{ width: 16, height: 16 }} />
                      AP Invoices
                    </NavLink>
                    <NavLink
                      to="/vendors"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                    >
                      <UserGroupIcon style={{ width: 16, height: 16 }} />
                      Vendor Management
                    </NavLink>
                  </div>
                )}
              </div>
            )}
            {/* HR Dropdown */}
            {canViewEmployees && (
              <div
                className="nav-dropdown"
                onMouseEnter={handleHrMouseEnter}
                onMouseLeave={handleHrMouseLeave}
              >
                <button className={`nav-link ${location.pathname.includes('/employees') ? 'active' : ''}`}>
                  <UsersIcon style={{ width: 18, height: 18 }} />
                  HR
                  <ChevronDownIcon style={{ width: 14, height: 14, marginLeft: 4 }} />
                </button>
                {hrDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <NavLink
                      to="/employees"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                    >
                      <UsersIcon style={{ width: 16, height: 16 }} />
                      Employees
                    </NavLink>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="navbar-user">
          <div
            className="nav-dropdown"
            onMouseEnter={handleUserMouseEnter}
            onMouseLeave={handleUserMouseLeave}
          >
            <button className={`nav-link ${['/my-profile', '/change-password', '/direct-reports'].includes(location.pathname) ? 'active' : ''}`}>
              <UserCircleIcon style={{ width: 18, height: 18 }} />
              {user?.full_name}
              <ChevronDownIcon style={{ width: 14, height: 14, marginLeft: 4 }} />
            </button>
            {userDropdownOpen && (
              <div className="nav-dropdown-menu" style={{ right: 0, left: 'auto', minWidth: '160px' }}>
                <NavLink
                  to="/my-profile"
                  className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                  onClick={() => setUserDropdownOpen(false)}
                >
                  <UserCircleIcon style={{ width: 16, height: 16 }} />
                  My Profile
                </NavLink>
                <NavLink
                  to="/change-password"
                  className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                  onClick={() => setUserDropdownOpen(false)}
                >
                  <KeyIcon style={{ width: 16, height: 16 }} />
                  Change Password
                </NavLink>
                {hasDirectReports && (
                  <NavLink
                    to="/direct-reports"
                    className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <UserGroupIcon style={{ width: 16, height: 16 }} />
                    Direct Reports
                  </NavLink>
                )}
              </div>
            )}
          </div>
          {user?.role === 'admin' && (
            <>
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => navigate('/audit-logs')}
                title="Audit Logs"
              >
                <ClockIcon style={{ width: 20, height: 20 }} />
              </button>
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => navigate('/settings')}
                title="Settings"
              >
                <Cog6ToothIcon style={{ width: 20, height: 20 }} />
              </button>
            </>
          )}
          <button className="btn btn-ghost btn-icon" onClick={handleLogout} title="Logout">
            <ArrowRightOnRectangleIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
